// apps/web/src/pages/spread/useSpreadConstraintEvaluation.ts
//
// Phase 9 Plan 15 (GAP CLOSURE) Task 1 — render-time constraint evaluation
// composition hook. Composes Plan 09-10's useConstraintEvaluation (debounced
// dispatcher hook) with Task 1's buildSpreadEvalContext (pure factory) so the
// SpreadRenderPane subscribes to a single hook and gets back the dispatcher's
// EvaluationResult.
//
// CSTR-02 ("pure evaluate runs at edit time AND render time") closure: this is
// the render-time call site that the verifier reported missing
// (09-VERIFICATION.md SC#2 / CSTR-02). After Plan 09-15 lands, grep
// `useConstraintEvaluation` across apps/web/src/ returns at least 2 production
// call sites (Constraints.tsx edit-time + this file render-time), satisfying
// the CSTR-02 contract.
//
// AUTHR-06 hygiene: this hook adds NO new useWatch subscriptions and NO new
// form-state reads. It is a pure composition of (constraints, input) → through
// useMemo for EvalContext stability → through useConstraintEvaluation's 100ms
// debounce → out as EvaluationResult. The dispatcher itself is bounded by
// CSTR-08 (250-constraint stress under 16ms).
//
// INVARIANT — useMemo identity-stability: the EvalContext object reference is
// stable across renders whose inputs did not change. Without this, every
// render would build a fresh EvalContext object, defeat the
// useConstraintEvaluation effect's referential-equality check, and force a
// new debounced evaluate() call per render. The memoization keys cover every
// field buildSpreadEvalContext reads.

import { useMemo } from "react";
import type {
  Constraint,
  EvaluationResult,
} from "@the-recipe-spread/shared";
import { useConstraintEvaluation } from "@/lib/constraints/useConstraintEvaluation";
import {
  buildSpreadEvalContext,
  type SpreadEvalContextInput,
} from "./buildSpreadEvalContext";

export function useSpreadConstraintEvaluation(
  constraints: ReadonlyArray<Constraint>,
  input: SpreadEvalContextInput,
): EvaluationResult {
  // identity-stable EvalContext keyed on (recipe.id, batchSets ref,
  // activeBatchSetIndex, widgetStates ref). Re-built only when one of these
  // changes — the buildSpreadEvalContext factory is pure so a stable input
  // tuple guarantees a stable output reference.
  const recipeWithId = input.recipe as { id?: string };
  const recipeId = typeof recipeWithId.id === "string" ? recipeWithId.id : "";
  const context = useMemo(
    () => buildSpreadEvalContext(input),
    // The exhaustive-deps lint is intentionally narrow here — we depend on
    // these specific reference + scalar identities, not the input object as a
    // whole (a fresh input wrapper per render would defeat the memo). Each
    // listed dep is a primitive or a ReadonlyMap/Array reference; identity
    // changes only when the actual contents change.
    // biome-ignore lint/correctness/useExhaustiveDependencies: see above
    [recipeId, input.batchSets, input.activeBatchSetIndex, input.widgetStates],
  );
  return useConstraintEvaluation(constraints, context);
}
